import { Injectable, Injector } from '@angular/core';
import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { deleteObject, FirebaseStorage, getDownloadURL, getStorage, ref, uploadBytesResumable, UploadTask } from 'firebase/storage';
import { ENV } from '../../../commons/injection.token';
import { StorageInterface } from '../storage.interface';
import { UploadTypeEnum } from '../upload.type.enum';

/**
 * Firebase Storage through the modular Firebase SDK.
 *
 * The app is resolved from the `firebase` block of the application's environment (the `ENV`
 * token). The default app may already exist — the chat push service initializes it with the
 * same configuration — so it is reused instead of initialized twice.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseService implements StorageInterface {

  private readonly storageBucket: string;

  private readonly storage: FirebaseStorage;

  constructor(injector: Injector) {
    const options = injector.get(ENV)['firebase'] as FirebaseOptions;
    this.storageBucket = options.storageBucket;
    this.storage = getStorage(FirebaseService.resolveApp(options));
  }

  private static resolveApp(options: FirebaseOptions): FirebaseApp {
    return getApps().length > 0 ? getApp() : initializeApp(options);
  }

  /** Uploads a file and resolves `{ url }` with its download URL. */
  uploadFile(file: File, filename: string, uploadTypeEnum: UploadTypeEnum): Promise<any> {
    try {
      return this.startUpload(uploadTypeEnum.toString() + filename, file).then(
        (snapshot) => getDownloadURL(snapshot.ref).then((url) => ({ url })),
        (err) => {
          console.warn('Firebase Storage upload failed');
          console.error(JSON.stringify(err));
          throw err;
        },
      );
    } catch (error) {
      console.error(error);
    }
    return Promise.resolve(null);
  }

  /** Starts a resumable upload; callers that show progress or allow cancelling keep the task. */
  startUpload(filePath: string, file: File): UploadTask {
    return uploadBytesResumable(ref(this.storage, filePath), file);
  }

  /** Download URL of the object a finished upload task wrote. */
  getDownloadUrl(task: UploadTask): Promise<string> {
    return getDownloadURL(task.snapshot.ref);
  }

  /**
   * Deletes a file of this project's bucket; URLs from elsewhere are left alone.
   *
   * Never rejects and resolves right away, while the deletion runs in the background:
   * callers clear the image as soon as this resolves, and a failed deletion is only logged.
   */
  deleteFile(storageUrl: string) {
    return new Promise<any>((resolve) => {
      try {
        if (storageUrl.indexOf(this.storageBucket) !== -1) {
          deleteObject(ref(this.storage, storageUrl)).catch((error) => console.error(error));
          resolve(undefined);
        } else {
          resolve(storageUrl);
        }
      } catch (error) {
        console.error(error);
        resolve(error);
      }
    });
  }

}
